/**
 * Auth Hooks - T038
 *
 * React Query hooks for authentication
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { authApi, ApiError } from '../services/api';

export interface User {
  id: string;
  email: string;
  name: string;
}

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: Error | null;
}

// Query key for auth state
export const AUTH_QUERY_KEY = ['auth', 'me'] as const;

/**
 * Hook to get current authentication state
 */
export function useAuth(): AuthState {
  const { data, isLoading, error } = useQuery({
    queryKey: AUTH_QUERY_KEY,
    queryFn: authApi.me,
    retry: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  return {
    user: data?.user ?? null,
    isAuthenticated: !!data?.user,
    isLoading,
    error: error as Error | null,
  };
}

/**
 * Hook for login mutation
 */
export function useLogin() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      authApi.login(email, password),
    onSuccess: async (data) => {
      // A new login must not inherit the previous account's tenant/data cache.
      await queryClient.cancelQueries();
      queryClient.clear();
      queryClient.setQueryData(AUTH_QUERY_KEY, data);
      // Navigate to home
      navigate({ to: '/' });
    },
    onError: (error: ApiError) => {
      console.error('Login failed:', error);
    },
  });
}

/**
 * Hook for logout mutation
 */
export function useLogout() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: authApi.logout,
    onSuccess: async () => {
      // Stop older requests before clearing data, then retain an explicit
      // anonymous state so mounted guards cannot flash the previous account.
      await queryClient.cancelQueries();
      queryClient.clear();
      queryClient.setQueryData(AUTH_QUERY_KEY, { user: null, session: null });
      queryClient.setQueryData(['session'], null);
      navigate({ to: '/login', replace: true });
    },
    onError: (error: ApiError) => {
      console.error('Logout failed:', error);
      // Keep the current state and show a retry: a failed server request
      // has not signed the user out and must not be presented as success.
    },
  });
}

/**
 * Hook to check if user is authenticated (for route guards)
 */
export function useRequireAuth() {
  const { user, isLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  if (!isLoading && !isAuthenticated) {
    navigate({ to: '/login' });
  }

  return { user, isLoading, isAuthenticated };
}
